import { PostStatus } from '../enums';
import { topicsOverlap } from '../topics/topic.utils';

/** Criteria for items eligible to appear in the public feed. */
export interface FeedEligibilityCriteria {
  postStatus: PostStatus;
  excludeDisabledCreators: boolean;
}

/** Default pre-MVP feed source: published posts from active creators. */
export function getDefaultFeedCriteria(): FeedEligibilityCriteria {
  return {
    postStatus: PostStatus.PUBLISHED,
    excludeDisabledCreators: true,
  };
}

export interface FeedRankableItem {
  createdAt: Date;
}

/** Default ranking: chronological (newest first). Replace for future algorithms. */
export function rankFeedItems<T extends FeedRankableItem>(items: T[]): T[] {
  return [...items].sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
}

export const HOME_FEED_FOLLOW_BOOST = 1000;
export const HOME_FEED_INTEREST_BOOST = 500;
export const HOME_FEED_AUTHOR_HIDE_PENALTY = 300;
export const HOME_FEED_MAX_AUTHOR_PENALTY = 900;
export const HOME_FEED_MAX_QUALITY_BOOST = 100;

export type PostEngagementCounts = {
  likes?: number;
  comments?: number;
  bookmarks?: number;
  reposts?: number;
};

/**
 * Small quality prior used by For You. Deliberate actions are worth more than a
 * like, but the result is capped so popularity cannot override personal relevance.
 */
export function computePostQualityBoost(counts: PostEngagementCounts): number {
  const weighted =
    Math.max(counts.likes ?? 0, 0) +
    Math.max(counts.comments ?? 0, 0) * 3 +
    Math.max(counts.bookmarks ?? 0, 0) * 4 +
    Math.max(counts.reposts ?? 0, 0) * 5;
  return Math.min(weighted, HOME_FEED_MAX_QUALITY_BOOST);
}

export type HomeFeedScoreInput = {
  createdAt: Date;
  authorId: string;
  followedAuthorIds: Set<string>;
  viewerTopicKeys: Set<string>;
  authorTopics?: string[] | null;
  authorHideCount?: number;
  engagement?: PostEngagementCounts;
};

/** Rule-based home feed score with follow/topic boosts and hide-feedback penalties. */
export function computeHomeFeedScore(input: HomeFeedScoreInput): number {
  let score = input.createdAt.getTime() / 1_000_000;
  if (input.followedAuthorIds.has(input.authorId)) score += HOME_FEED_FOLLOW_BOOST;
  if (input.viewerTopicKeys.size > 0 && topicsOverlap(input.viewerTopicKeys, input.authorTopics)) {
    score += HOME_FEED_INTEREST_BOOST;
  }
  const authorPenalty = Math.min(
    (input.authorHideCount ?? 0) * HOME_FEED_AUTHOR_HIDE_PENALTY,
    HOME_FEED_MAX_AUTHOR_PENALTY,
  );
  const qualityBoost = computePostQualityBoost(input.engagement ?? {});
  return score + qualityBoost - authorPenalty;
}
